import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    BackendAPIBlueprint,
    BackendAPITypes,
    usePartner__computeVatDeclaration,
    usePartner__getAllVatDeclarationSnapshots,
    usePartner__getVatDeclarationSnapshot,
    usePartner__requestVatDeclarationApprovalMutation,
    usePartner__saveVatDeclarationSnapshotMutation,
    usePartner__withdrawVatDeclarationApprovalRequestMutation,
} from '../../../../../../generated/blueprint.ts';
import { QueryKey } from '../../../../../../api/queryKeys.ts';
import { useDocumentChanged } from '../../../../../../views/DocumentDrawer';
import type { VatReturnDraftPeriod, VatReturnPeriod, VatReturnSelection } from './types/index.ts';
import { groupVatReturnPeriods, vatReturnPeriodKey } from './utils/index.ts';

export type ComputedVatDeclaration = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerComputeVatDeclarationGetResponseBody;
export type SavedVatDeclarationSnapshot = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerGetVatDeclarationSnapshotGetResponseBody;
export type CreatedVatDeclarationSnapshot = BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerSaveVatDeclarationSnapshotPostResponseBody;

export interface VatReturnQueryState<T> {
    data: T | undefined;
    isLoading: boolean;
    isError: boolean;
}

export interface VatReturnDraftState extends VatReturnQueryState<ComputedVatDeclaration> {
    isRefreshing: boolean;
}

export interface VatReturnsState {
    periods: VatReturnPeriod[];
    isLoadingSnapshots: boolean;
    isFetchingSnapshots: boolean;
    hasSnapshotsLoadError: boolean;
    selection: VatReturnSelection | null;
    selectedPeriod: VatReturnPeriod | null;
    selectPeriod: (periodKey: string) => void;
    selectDraft: (periodKey: string) => void;
    selectVersion: (periodKey: string, snapshotId: string) => void;
    startDraft: (dateFrom: string, dateTo: string) => void;
    discardDraft: (periodKey: string) => void;
    refreshDrafts: () => void;
    draft: VatReturnDraftState;
    snapshot: VatReturnQueryState<SavedVatDeclarationSnapshot>;
    saveDraft: () => Promise<CreatedVatDeclarationSnapshot>;
    isSaving: boolean;
    requestApproval: (snapshotId: string) => Promise<void>;
    isRequestingApproval: boolean;
    withdrawApproval: (requestId: string) => Promise<void>;
    isWithdrawingApproval: boolean;
}

interface LocalVatReturnState {
    drafts: VatReturnDraftPeriod[];
    selection: VatReturnSelection | null;
}

const EMPTY_LOCAL_STATE: LocalVatReturnState = { drafts: [], selection: null };

function defaultSelection(period: VatReturnPeriod | undefined): VatReturnSelection | null {
    if (!period) {
        return null;
    }
    if (period.hasDraft) {
        return { periodKey: period.key, kind: 'draft' };
    }
    if (period.latestVersion) {
        return { periodKey: period.key, kind: 'version', snapshotId: period.latestVersion.id };
    }
    return null;
}

function fallbackSelection(chosen: VatReturnSelection, periods: VatReturnPeriod[]): VatReturnSelection | null {
    return defaultSelection(periods.find((period) => period.key === chosen.periodKey)) ?? defaultSelection(periods[0]);
}

const REPORT_ERRORS_TO_SENTRY = { reportErrorsToSentry: true };

function isDraftStillSelected(selection: VatReturnSelection | null, draftKey: string): boolean {
    return selection === null || (selection.kind === 'draft' && selection.periodKey === draftKey);
}

function isSelectable(selection: VatReturnSelection, period: VatReturnPeriod | undefined): boolean {
    if (!period) {
        return false;
    }
    if (selection.kind === 'draft') {
        return period.hasDraft;
    }
    return period.versions.some((version) => version.id === selection.snapshotId);
}

export function useVatReturns(
    api: BackendAPIBlueprint,
    businessId: string | undefined,
    isActive: boolean,
): VatReturnsState {
    const queryClient = useQueryClient();
    const [localByBusinessId, setLocalByBusinessId] = useState<Record<string, LocalVatReturnState>>({});
    const local = (businessId && localByBusinessId[businessId]) || EMPTY_LOCAL_STATE;
    const drafts = local.drafts;
    const chosenSelection = local.selection;
    const openedForBusinessId = isActive ? businessId : undefined;
    const [lastOpenedForBusinessId, setLastOpenedForBusinessId] = useState(openedForBusinessId);
    if (lastOpenedForBusinessId !== openedForBusinessId) {
        setLastOpenedForBusinessId(openedForBusinessId);
        if (openedForBusinessId) {
            setLocalByBusinessId((previous) => ({
                ...previous,
                [openedForBusinessId]: { ...(previous[openedForBusinessId] ?? EMPTY_LOCAL_STATE), selection: null },
            }));
        }
    }

    const changeLocalFor = useCallback((
        forBusinessId: string,
        change: (current: LocalVatReturnState) => Partial<LocalVatReturnState>,
    ) => {
        setLocalByBusinessId((previous) => {
            const current = previous[forBusinessId] ?? EMPTY_LOCAL_STATE;
            return { ...previous, [forBusinessId]: { ...current, ...change(current) } };
        });
    }, []);

    const changeLocal = useCallback((change: (current: LocalVatReturnState) => Partial<LocalVatReturnState>) => {
        if (businessId) {
            changeLocalFor(businessId, change);
        }
    }, [businessId, changeLocalFor]);

    const snapshotsQuery = usePartner__getAllVatDeclarationSnapshots(
        api,
        { client_business_id: businessId ?? '' },
        {
            queryKey: QueryKey.VatDeclarationSnapshots,
            enabled: Boolean(businessId) && isActive,
            meta: REPORT_ERRORS_TO_SENTRY,
        },
    );
    const snapshots = snapshotsQuery.data?.snapshots;

    const periods = useMemo(() => groupVatReturnPeriods(snapshots ?? [], drafts), [snapshots, drafts]);

    const selection = useMemo(() => {
        if (!chosenSelection) {
            return null;
        }
        if (snapshotsQuery.isLoading || snapshotsQuery.isError || isSelectable(chosenSelection, periods.find((period) => period.key === chosenSelection.periodKey))) {
            return chosenSelection;
        }
        return fallbackSelection(chosenSelection, periods);
    }, [chosenSelection, periods, snapshotsQuery.isLoading, snapshotsQuery.isError]);

    const selectedPeriod = periods.find((period) => period.key === selection?.periodKey) ?? null;
    const selectedDraft = useMemo<VatReturnDraftPeriod | null>(() => (
        selection?.kind === 'draft' && selectedPeriod
            ? { dateFrom: selectedPeriod.periodStart, dateTo: selectedPeriod.periodEnd }
            : null
    ), [selection, selectedPeriod]);
    const selectedSnapshotId = selection?.kind === 'version' ? selection.snapshotId : null;

    const draftQuery = usePartner__computeVatDeclaration(
        api,
        {
            client_business_id: businessId ?? '',
            date_from: selectedDraft?.dateFrom ?? '',
            date_to: selectedDraft?.dateTo ?? '',
        },
        {
            queryKey: QueryKey.VatDeclarationDraft,
            enabled: Boolean(businessId) && selectedDraft !== null && isActive,
            meta: REPORT_ERRORS_TO_SENTRY,
        },
    );

    const snapshotQuery = usePartner__getVatDeclarationSnapshot(
        api,
        { client_business_id: businessId ?? '', snapshot_id: selectedSnapshotId ?? '' },
        {
            queryKey: QueryKey.VatDeclarationSnapshots,
            enabled: Boolean(businessId) && selectedSnapshotId !== null && isActive,
            meta: REPORT_ERRORS_TO_SENTRY,
        },
    );

    const refreshSavedReturns = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: [QueryKey.VatDeclarationSnapshots] });
    }, [queryClient]);
    const saveMutation = usePartner__saveVatDeclarationSnapshotMutation(api, {
        invalidates: [QueryKey.VatDeclarationSnapshots],
        onError: refreshSavedReturns,
    });
    const requestApprovalMutation = usePartner__requestVatDeclarationApprovalMutation(api, {
        invalidates: [QueryKey.VatDeclarationSnapshots],
        onError: refreshSavedReturns,
    });

    const withdrawApprovalMutation = usePartner__withdrawVatDeclarationApprovalRequestMutation(api, {
        invalidates: [QueryKey.VatDeclarationSnapshots],
        onError: refreshSavedReturns,
    });

    const selectPeriod = useCallback((periodKey: string) => {
        const period = periods.find((candidate) => candidate.key === periodKey);
        const next = defaultSelection(period);
        if (next) {
            changeLocal(() => ({ selection: next }));
        }
    }, [periods, changeLocal]);

    const selectDraft = useCallback((periodKey: string) => {
        changeLocal(() => ({ selection: { periodKey, kind: 'draft' } }));
    }, [changeLocal]);

    const selectVersion = useCallback((periodKey: string, snapshotId: string) => {
        changeLocal(() => ({ selection: { periodKey, kind: 'version', snapshotId } }));
    }, [changeLocal]);

    const startDraft = useCallback((dateFrom: string, dateTo: string) => {
        if (!businessId) {
            return;
        }
        void queryClient.resetQueries({
            queryKey: [
                QueryKey.VatDeclarationDraft,
                'partner/compute-vat-declaration',
                { client_business_id: businessId, date_from: dateFrom, date_to: dateTo },
            ],
            exact: true,
        });
        changeLocal((current) => {
            const hasDraft = current.drafts.some((draft) => draft.dateFrom === dateFrom && draft.dateTo === dateTo);
            return {
                drafts: hasDraft ? current.drafts : [...current.drafts, { dateFrom, dateTo }],
                selection: { periodKey: vatReturnPeriodKey(dateFrom, dateTo), kind: 'draft' },
            };
        });
    }, [businessId, queryClient, changeLocal]);

    const refreshDrafts = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: [QueryKey.VatDeclarationDraft] });
    }, [queryClient]);

    const discardDraft = useCallback((periodKey: string) => {
        const latestVersion = periods.find((period) => period.key === periodKey)?.latestVersion ?? null;
        const selectionAfterDiscard: VatReturnSelection | null = latestVersion
            ? { periodKey, kind: 'version', snapshotId: latestVersion.id }
            : null;
        changeLocal((current) => ({
            drafts: current.drafts.filter((draft) => vatReturnPeriodKey(draft.dateFrom, draft.dateTo) !== periodKey),
            selection: current.selection?.periodKey === periodKey && current.selection.kind === 'draft'
                ? selectionAfterDiscard
                : current.selection,
        }));
    }, [periods, changeLocal]);

    const { mutateAsync: saveSnapshot } = saveMutation;
    const saveDraft = useCallback(async (): Promise<CreatedVatDeclarationSnapshot> => {
        if (!businessId || !selectedDraft) {
            throw new Error('Cannot save a VAT return without a selected draft');
        }
        const saved = await saveSnapshot({
            client_business_id: businessId,
            date_from: selectedDraft.dateFrom,
            date_to: selectedDraft.dateTo,
        });
        const draftKey = vatReturnPeriodKey(selectedDraft.dateFrom, selectedDraft.dateTo);
        changeLocalFor(businessId, (current) => ({
            drafts: current.drafts.filter((draft) => vatReturnPeriodKey(draft.dateFrom, draft.dateTo) !== draftKey),
            selection: isDraftStillSelected(current.selection, draftKey)
                ? { periodKey: vatReturnPeriodKey(saved.period_start, saved.period_end), kind: 'version', snapshotId: saved.id }
                : current.selection,
        }));
        return saved;
    }, [businessId, selectedDraft, saveSnapshot, changeLocalFor]);

    const { mutateAsync: sendApprovalRequest } = requestApprovalMutation;
    const requestApproval = useCallback(async (snapshotId: string): Promise<void> => {
        if (!businessId) {
            throw new Error('Cannot request approval for a VAT return without a client business id');
        }
        await sendApprovalRequest({ client_business_id: businessId, snapshot_id: snapshotId });
    }, [businessId, sendApprovalRequest]);

    const { mutateAsync: sendWithdrawal } = withdrawApprovalMutation;
    const withdrawApproval = useCallback(async (requestId: string): Promise<void> => {
        if (!businessId) {
            throw new Error('Cannot withdraw a VAT return approval request without a client business id');
        }
        await sendWithdrawal({ client_business_id: businessId, request_id: requestId });
    }, [businessId, sendWithdrawal]);

    return {
        periods,
        isLoadingSnapshots: snapshotsQuery.isLoading,
        isFetchingSnapshots: snapshotsQuery.isFetching,
        hasSnapshotsLoadError: snapshotsQuery.isError,
        selection,
        selectedPeriod,
        selectPeriod,
        selectDraft,
        selectVersion,
        startDraft,
        discardDraft,
        refreshDrafts,
        draft: {
            data: selectedDraft ? draftQuery.data : undefined,
            isLoading: selectedDraft !== null && draftQuery.isLoading,
            isRefreshing: selectedDraft !== null && draftQuery.isFetching && !draftQuery.isLoading,
            isError: selectedDraft !== null && draftQuery.isError,
        },
        snapshot: {
            data: selectedSnapshotId ? snapshotQuery.data : undefined,
            isLoading: selectedSnapshotId !== null && snapshotQuery.isLoading,
            isError: selectedSnapshotId !== null && snapshotQuery.isError,
        },
        saveDraft,
        isSaving: saveMutation.isPending && saveMutation.variables?.client_business_id === businessId,
        requestApproval,
        isRequestingApproval: requestApprovalMutation.isPending
            && requestApprovalMutation.variables?.client_business_id === businessId,
        withdrawApproval,
        isWithdrawingApproval: withdrawApprovalMutation.isPending
            && withdrawApprovalMutation.variables?.client_business_id === businessId,
    };
}

export function useRefreshVatReturnDraftsOnDocumentChange(refreshDrafts: () => void): void {
    const documentChanged = useDocumentChanged();
    useEffect(() => {
        if (documentChanged) {
            refreshDrafts();
        }
    }, [documentChanged, refreshDrafts]);
}
